import moment from 'moment';

import {
  formatEntityId,
  formatSecondsToHHMM,
  toUtcOffsetDisplay,
  usDollarFormatter,
} from '../../../helpers';
import { asDefinedOr } from '../billings/claims/utils';
import { BTSessionStatus, ENTITY_PREFIX } from '../../../constants';
import { HistorySearchParams, QUESTIONNAIRE_SEARCH_PARAM_ALIASES } from '../../../utils/common';
import { CQAReviewStatus } from '../qa/constants';
import { ClinicalStatus } from '../../../admin/constants/general';
import { QUESTIONNAIRE_HISTORY_TYPE } from './constants';
import {
  RECOUPMENT_PROBABILITY_LABELS,
  RECOUPMENT_REASON_LABELS,
} from '../billings/claim-info/constants';

import type { ISearchConfigList } from '../../base/FHGenericSearch';
import type { Moment } from 'moment';
import type {
  IBtSupervision,
  IClaim,
  IClaimInvoice,
  IClient,
  IClientAssessment,
  IClientSupervision,
  IGroupSupervision,
  IParentSupervision,
  IProcedure,
  IProvider,
  ISessionMinified,
} from '../../../utils/types';
import type { IIntakeQuestionnaireBasic } from '../../../store/questionnaires/intake';
import type { IOnboardingQuestionnaireBasic } from '../../../store/questionnaires/onboarding';

export const claimHistoryDataTransformer = (claims: IClaim[]) => {
  return claims.map((claim: IClaim) => {
    return {
      id: claim.id,
      status: claim.status,
      secondary_status: claim.secondary_status,
      recoupment_probability: claim.recoupment_probability
        ? RECOUPMENT_PROBABILITY_LABELS[claim.recoupment_probability]
        : '',
      recoupment_reason: claim.recoupment_reason
        ? RECOUPMENT_REASON_LABELS[claim.recoupment_reason]
        : '',
      bay: claim.bay?.replace('BAY ', ''),
      claimed_date: claim.date_generated
        ? `${moment(claim.date_generated).format('MM/DD/YY')}`
        : '',
      client: `${claim.client?.first_name} ${claim.client?.last_name}`,
      created_by: `${claim.biller?.first_name} ${claim.biller?.last_name}`,
      bcba_name: `${claim.bcba?.first_name} ${claim.bcba?.last_name}`,
      claim_id: formatEntityId(String(claim.id), ENTITY_PREFIX.Claim),
      no_of_sessions: claim.num_procedures,
      clearing_house_claim_id: claim?.clearing_house_claim_id,
      earliest_service_date: claim?.earliest_date_of_service
        ? `${moment(claim?.earliest_date_of_service).format('MM/DD/YY')}`
        : '',
      service_code_label: claim?.service_code
        ? `${claim?.service_code?.code} (${claim?.service_code?.label})`
        : '',
      sessions: claim?.procedures?.map((procedure: IProcedure) => {
        return {
          id: `${procedure.procedure_id}`,
          date: moment(procedure.date_start).add(procedure.utc_offset_date_start).utc().format(),
          status: claim.status,
          selected: true,
          readonly: Boolean(claim.id),
          duration: procedure.duration / 3600,
          provider_name: `${claim.bcba.first_name} ${claim.bcba.last_name}`,
          provider_id: claim.bcba.id,
          timeZone: toUtcOffsetDisplay(asDefinedOr(procedure.utc_offset_date_start, 0)),
        };
      }),

      service_code: claim?.service_code,
    };
  });
};

export const assessmentHistoryDataTransformer = (claims: IClientAssessment[]) => {
  return claims.map((assessment: IClientAssessment) => {
    return {
      id: assessment.id,
      status: assessment.status,
      service_code: assessment.service_code,
      client: `${assessment.client?.first_name} ${assessment.client?.last_name}`,
      assessment_id: formatEntityId(String(assessment.id), ENTITY_PREFIX.ClientAssessmentNote),
      start_date: `${moment(assessment.date_start).format('MM/DD/YY')}`,
      time: moment(assessment.date_start).format('hh:mm a'),
      duration: formatSecondsToHHMM(Number(assessment.duration || 0)),
    };
  });
};

export const clientSNHistoryDataTransformer = (clientSNs: IClientSupervision[]) => {
  return clientSNs.map((clientSN: IClientSupervision) => {
    return {
      id: clientSN?.id,
      status: clientSN?.status,
      client: `${clientSN.client?.first_name} ${clientSN.client?.last_name}`,
      bt: clientSN.bt_participants?.primary
        ? `${clientSN.bt_participants?.primary?.first_name} ${clientSN.bt_participants?.primary?.last_name}`
        : 'NA',
      session_id: formatEntityId(String(clientSN?.id), ENTITY_PREFIX.ClientSupervisionNotes),
      date: `${moment(clientSN.date_start).format('MM/DD/YY')}`,
      time: moment(clientSN.date_start).format('hh:mm a'),
      duration: formatSecondsToHHMM(Number(clientSN.duration || 0)),
    };
  });
};

export const btSNHistoryDataTransformer = (btSNs: IBtSupervision[]) => {
  return btSNs.map((btSN: IBtSupervision) => {
    return {
      id: btSN?.id,
      status: btSN?.status,
      client: `${btSN.client?.first_name} ${btSN.client?.last_name}`,
      bt: btSN.bt_provider
        ? `${btSN.bt_provider?.first_name} ${btSN.bt_provider?.last_name}`
        : 'NA',
      session_id: formatEntityId(String(btSN?.id), ENTITY_PREFIX.BTSupervisionNotes),
      date: `${moment(btSN.date_start).format('MM/DD/YY')}`,
      time: moment(btSN.date_start).format('hh:mm a'),
      duration: formatSecondsToHHMM(Number(btSN.duration || 0)),
    };
  });
};

export const groupSNHistoryDataTransformer = (groupSNs: IGroupSupervision[]) => {
  return groupSNs.map((groupParentSN: IGroupSupervision) => {
    return {
      id: groupParentSN?.id,
      status: groupParentSN?.status,
      client: `${groupParentSN?.clients
        .map((item) => `${item.first_name} ${item.last_name}`)
        .join(', ')}`,
      session_id: formatEntityId(String(groupParentSN?.id), ENTITY_PREFIX.GroupSupervisionNotes),
      date: `${moment(groupParentSN.date_start).format('MM/DD/YY')}`,
      time: moment(groupParentSN.date_start).format('hh:mm a'),
      duration: formatSecondsToHHMM(Number(groupParentSN.duration || 0)),
    };
  });
};

export const parentSNHistoryDataTransformer = (parentSNs: IParentSupervision[]) => {
  return parentSNs.map((parentSN: IParentSupervision) => {
    return {
      id: parentSN?.id,
      status: parentSN?.status,
      client: `${parentSN.client?.first_name} ${parentSN.client?.last_name}`,
      session_id: formatEntityId(String(parentSN?.id), ENTITY_PREFIX.ParentSupervisionNotes),
      date: `${moment(parentSN.date_start).format('MM/DD/YY')}`,
      time: moment(parentSN.date_start).format('hh:mm a'),
      duration: formatSecondsToHHMM(Number(parentSN.expected_duration || 0)),
    };
  });
};

export const sessionHistoryDataTransformer = (sessions: ISessionMinified[]) => {
  return sessions.map((session: ISessionMinified) => {
    return {
      id: session?.id,
      status:
        session.status === BTSessionStatus.AwaitingReview &&
        session?.clinical_qa_review?.status === CQAReviewStatus.Acknowledged
          ? BTSessionStatus.AwaitingFinalization
          : session.status,
      client: `${session.client?.first_name} ${session.client?.last_name}`,
      session_id: formatEntityId(String(session?.id), ENTITY_PREFIX.SessionNotes),
      date: `${moment(session.date_start).format('MM/DD/YY')}`,
      time: moment(session.date_start).format('hh:mm a'),
      duration: formatSecondsToHHMM(Number(session.expected_duration || 0)),
    };
  });
};

export const invoiceHistoryDataTransformer = (invoices: IClaimInvoice[]) => {
  return invoices.map((invoice: IClaimInvoice) => {
    return {
      id: invoice.id,
      status: invoice.status,
      created_by: `${invoice.biller?.first_name} ${invoice.biller?.last_name}`,
      billed_to: `${invoice.payee?.first_name} ${invoice.payee?.last_name}`,
      client_name: `${invoice.client?.first_name} ${invoice.client?.last_name}`,
      invoice_id: formatEntityId(String(invoice.id), ENTITY_PREFIX.Invoice),
      amount_billed: usDollarFormatter.format(invoice.amount_billed),
      amount_due: usDollarFormatter.format(invoice.amount_due),
      date_sent: invoice.date_sent ? `${moment(invoice.date_sent).format('MM/DD/YY')}` : '',
      date_due: invoice.date_due ? `${moment(invoice.date_due).format('MM/DD/YY')}` : '',
    };
  });
};

export const questionnaireHistoryDataTransformer = (
  onboardingQuestionnaires: IOnboardingQuestionnaireBasic[],
  intakeQuestionnaires: IIntakeQuestionnaireBasic[],
) => {
  const taggedQuestionnaires = [
    ...onboardingQuestionnaires.map((questionnaire) => ({
      questionnaire,
      type: QUESTIONNAIRE_HISTORY_TYPE.ONBOARDING,
    })),
    ...intakeQuestionnaires.map((questionnaire) => ({
      questionnaire,
      type: QUESTIONNAIRE_HISTORY_TYPE.INTAKE,
    })),
  ];

  return taggedQuestionnaires.map(({ questionnaire, type }) => ({
    id: questionnaire.id,
    questionnaire_id: formatEntityId(
      String(questionnaire.id),
      type === QUESTIONNAIRE_HISTORY_TYPE.ONBOARDING
        ? ENTITY_PREFIX.OnboardingQuestionnaire
        : ENTITY_PREFIX.IntakeQuestionnaire,
    ),
    type,
    status: questionnaire.status,
    client: `${questionnaire.client?.first_name} ${questionnaire.client?.last_name}`,
    provider: `${questionnaire.provider?.first_name} ${questionnaire.provider?.last_name}`,
    date_sent: questionnaire.date_sent ? moment(questionnaire.date_sent).format('MM/DD/YY') : '',
    date_concluded: questionnaire.date_concluded
      ? moment(questionnaire.date_concluded).format('MM/DD/YY')
      : '',
  }));
};

export function getQuestionnaireLoadPlan(params: Record<string, string | Moment>) {
  const { [HistorySearchParams.QuestionnaireType]: selectedType, ...rest } = params;
  const questionnaireParams = Object.fromEntries(
    Object.entries(rest).map(([key, value]) => [
      QUESTIONNAIRE_SEARCH_PARAM_ALIASES[key] ?? key,
      value,
    ]),
  );
  const loadOnboarding = !selectedType || selectedType === QUESTIONNAIRE_HISTORY_TYPE.ONBOARDING;
  const loadIntake = !selectedType || selectedType === QUESTIONNAIRE_HISTORY_TYPE.INTAKE;
  return { questionnaireParams, loadOnboarding, loadIntake };
}

export function getClientStatusDescription(client: IClient, populatePreClinical: boolean) {
  // Only surface the clinical status as a description for non-active clients; offboarded and
  // on-hold clients always show it, while pre-clinical is opt-in via populatePreClinical so it's
  // hidden in contexts that shouldn't expose pre-clinical clients. Active clients show nothing.
  const showStatus =
    client.clinical_status === ClinicalStatus.OFFBOARDED ||
    client.clinical_status === ClinicalStatus.ON_HOLD ||
    (populatePreClinical && client.clinical_status === ClinicalStatus.PRE_CLINICAL);
  return showStatus ? client.clinical_status : '';
}

export function populateClients(
  searchConfig: ISearchConfigList,
  clients: IClient[],
  populatePreClinical = false,
) {
  const updatedConfig = [...searchConfig];
  const clientConfig = updatedConfig.find((item) => item.id === HistorySearchParams.ClientId);
  if (clientConfig) {
    clientConfig.options = [
      ...clients.map((client: IClient) => ({
        id: client.id ?? '',
        label: `${client.first_name} ${client.last_name}`,
        description: getClientStatusDescription(client, populatePreClinical),
      })),
    ];
  }
  return updatedConfig;
}

export function populateProviders(
  searchConfig: ISearchConfigList,
  providers: IProvider[],
  fieldId: string,
) {
  const updatedConfig = [...searchConfig];
  const providerConfig = updatedConfig.find((item) => item.id === fieldId);
  if (providerConfig) {
    providerConfig.options = [
      ...providers.map((provider: IProvider) => ({
        id: provider.id ?? '',
        label: `${provider.first_name} ${provider.last_name}`,
      })),
    ];
  }
  return updatedConfig;
}
